import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { sectionRenderer, morphSection, buildSectionSelector } from '@theme/section-renderer';

/**
 * One-click add for suggested pieces, in the cart and in the product page's "Complete the
 * look" section. Adds the chosen variant, then hands the re-rendered cart sections to the
 * cart items components through a cart event, the same way the product form does.
 *
 * With the `stay-on-page` attribute the cart drawer stays closed: the component's own
 * section (`data-section-id`) is re-rendered in place so its prices follow the new cart,
 * and a confirmation with a "View cart" button shows above the pieces.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [status] - Polite live region for the confirmation.
 * @property {HTMLButtonElement} [viewCart] - Opens the cart once something was added.
 * @property {HTMLElement} [list] - The suggested pieces.
 *
 * @extends {Component<Refs>}
 */
class CartUpsellComponent extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#activeFetch?.abort();
  }

  /**
   * Adds the submitted piece to the cart.
   * @param {SubmitEvent} event - The submit event of a suggestion form.
   */
  async add(event) {
    event.preventDefault();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const stayOnPage = this.hasAttribute('stay-on-page');
    const button = form.querySelector('button[type="submit"]');
    if (button instanceof HTMLButtonElement) button.disabled = true;

    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;

    const formData = new FormData(form);
    const sectionIds = [];
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) sectionIds.push(element.dataset.sectionId);
    }
    formData.append('sections', sectionIds.join(','));

    try {
      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
        signal: abortController.signal,
      });
      const result = await response.json();

      if (result.status) {
        this.dispatchEvent(new CartErrorEvent(form.id, result.message, result.description, result.errors));
        if (button instanceof HTMLButtonElement) button.disabled = false;
        return;
      }

      const cart = await fetch(`${Theme.routes.cart_url}.js`, { signal: abortController.signal }).then((cartResponse) =>
        cartResponse.json()
      );

      this.dispatchEvent(
        new CartAddEvent(cart, String(formData.get('id')), {
          source: 'cart-upsell-component',
          itemCount: cart.item_count,
          productId: form.dataset.productId,
          sections: result.sections,
          openCart: !stayOnPage,
        })
      );

      if (stayOnPage) {
        await this.#refreshInPlace(form.dataset.addedMessage ?? '', abortController.signal);
      } else {
        this.#restoreFocus();
      }

      // The form can survive the update, so let the piece be added again.
      if (button instanceof HTMLButtonElement && button.isConnected) button.disabled = false;
    } catch (error) {
      if (abortController.signal.aborted) return;
      console.error(error);
      if (button instanceof HTMLButtonElement) button.disabled = false;
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }
  }

  /**
   * Opens the cart drawer, or the cart page when the theme has no drawer.
   */
  openCart() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && 'open' in drawer && typeof drawer.open === 'function') {
      drawer.open();
    } else {
      window.location.assign(Theme.routes.cart_url);
    }
  }

  /**
   * Re-renders this component's section so its prices and pieces follow the new cart, then
   * confirms the add in place. When nothing can be paired any more the section renders
   * empty, so the pieces are hidden instead, keeping the confirmation on screen.
   * @param {string} message - Confirmation for the piece that was added.
   * @param {AbortSignal} signal - Aborted when another add starts.
   */
  async #refreshInPlace(message, signal) {
    const { sectionId } = this.dataset;

    if (sectionId) {
      try {
        const html = await sectionRenderer.getSectionHTML(sectionId, false);
        if (signal.aborted) return;

        const nextSection = new DOMParser()
          .parseFromString(html, 'text/html')
          .getElementById(buildSectionSelector(sectionId));

        if (nextSection?.querySelector('[ref="list"]')) {
          await morphSection(sectionId, html);
        } else {
          this.querySelector('[ref="list"]')?.setAttribute('hidden', '');
        }
      } catch (error) {
        console.error(error);
      }
    }

    // Refs refresh a microtask after a morph, so read the live nodes directly.
    const status = this.querySelector('[ref="status"]');
    const viewCart = this.querySelector('[ref="viewCart"]');
    if (status) status.textContent = message;
    if (!(viewCart instanceof HTMLButtonElement)) return;

    viewCart.hidden = false;
    const active = document.activeElement;
    if (!active || active === document.body || !active.isConnected || active.closest('[hidden]')) viewCart.focus();
  }

  /**
   * The re-rendered cart drops the suggestion that was just added, along with the focused
   * button, so move focus to the checkout button once the update has landed.
   */
  #restoreFocus() {
    setTimeout(() => {
      const active = document.activeElement;
      if (active && active !== document.body && active.isConnected) return;

      const dialog = document.querySelector('.cart-drawer__dialog[open]');
      const checkoutButton = (dialog ?? document).querySelector('.cart__checkout-button');
      if (checkoutButton instanceof HTMLElement) checkoutButton.focus();
    }, 400);
  }
}

if (!customElements.get('cart-upsell-component')) {
  customElements.define('cart-upsell-component', CartUpsellComponent);
}
